<template>
    <div class="mt-5 w-full">
        <span
            v-if="productStore.forceSizeSelectionActive && !canProceedWithLaunchedProject && !productStore.isMediaclipConfiguratorProduct"
            class="mb-3 block text-center text-gray-500 italic"
            data-test-id="pdp-force-size-message"
            v-text="$t('pages.product_detail_page.product_info.product_button.force_size_selection_message')"
        />
        <StickyMobileButton>
            <div class="flex items-center gap-3">
                <div class="min-w-0 flex-1">
                    <Tooltip
                        ref="sizeSelectionTooltip"
                        :content="$t('pages.product_detail_page.product_info.product_button.force_size_selection_tooltip')"
                        :delay-duration="0"
                        :disabled="!needsSizeSelection"
                        content-class="rounded-md px-3 py-2 text-center text-base"
                    >
                        <div class="relative w-full" data-test-id="pdp-order-buttons">
                            <div v-if="canProceedWithLaunchedProject" class="flex w-full flex-col items-center gap-1">
                                <ProceedConfigurationButton />
                            </div>
                            <UploadButton v-else-if="productStore.isMediaclipConfiguratorProduct" :disabled="productStore.forceSizeSelectionActive" />
                            <!-- Ahead of the collection branch: a set is no configurator and
                         no collection, and adds one line per position. A meerluik uses
                         the same button; the server sells it as one line. -->
                            <ProductSetButton v-else-if="productStore.isSetBackedProduct" :disabled="setStore.forceSelectionActive" />
                            <ProductCollectionButton v-else-if="productStore.isCollectionProduct" :disabled="productStore.forceSizeSelectionActive" />
                            <StartOrderingButton v-else :disabled="productStore.forceSizeSelectionActive" />
                            <StartOverButton v-if="canProceedWithLaunchedProject" :disabled="productStore.forceSizeSelectionActive" />

                            <!-- Catches clicks on the disabled button; .stop keeps the tooltip shut until scrolled. -->
                            <div
                                v-if="needsSizeSelection"
                                class="absolute inset-0 cursor-not-allowed"
                                data-test-id="pdp-force-size-overlay"
                                @click.stop="scrollToSizeSelection"
                            />
                        </div>
                    </Tooltip>
                </div>
            </div>
        </StickyMobileButton>

        <span
            v-if="productStore.isMediaclipConfiguratorProduct && !canProceedWithLaunchedProject"
            class="mt-2 block text-center text-gray-500 italic"
            v-text="$t('pages.product_detail_page.after_upload_text')"
        />
    </div>
</template>

<script lang="ts" setup>
import StickyMobileButton from '@/components/buttons/StickyMobileButton.vue';
import Tooltip from '@/components/tooltips/Tooltip.vue';
import ProductCollectionButton from '@/pages/product-page/partials/product-info/partials/buttons/ProductCollectionButton.vue';
import ProductSetButton from '@/pages/product-page/partials/product-info/partials/set/ProductSetButton.vue';
import ProceedConfigurationButton from '@/pages/product-page/partials/product-info/partials/ProceedConfigurationButton.vue';
import StartOrderingButton from '@/pages/product-page/partials/product-info/partials/StartOrderingButton.vue';
import StartOverButton from '@/pages/product-page/partials/product-info/partials/StartOverButton.vue';
import UploadButton from '@/pages/product-page/partials/product-info/partials/UploadButton.vue';
import { useProductSetStore } from '@/stores/products/ProductSetStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { useMediaclipSessionStore } from '@/stores/tool/MediaclipSessionStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';

const productStore = useProductStore();
const setStore = useProductSetStore();
const sessionStore = useMediaclipSessionStore();

// isMounted ensures the session-dependent check is never evaluated during SSR
// or the initial hydration pass. sessionStorage is restored synchronously on
// the client before hydration, so without this guard the SSR HTML (which always
// has isActive=false) would not match the client's initial render for users
// with an active Mediaclip session, causing a hydration mismatch.
const isMounted = ref(false);
onMounted(() => {
    isMounted.value = true;
});

const canProceedWithLaunchedProject = computed<boolean>(
    () => isMounted.value && sessionStore.isActive && sessionStore.launchedProductSku === productStore.selectedProduct.product.sku,
);

// A set forces its selection through the set store; the filter-based check never fires for one.
const needsSizeSelection = computed<boolean>(
    () =>
        (productStore.isSetBackedProduct ? setStore.forceSelectionActive : productStore.forceSizeSelectionActive) &&
        !canProceedWithLaunchedProject.value,
);

// Scrolling counts as done once no scroll event fired for this long; scrollend is missing in older Safari
const scrollSettleMs = 150;

const sizeSelectionTooltip = useTemplateRef<InstanceType<typeof Tooltip>>('sizeSelectionTooltip');

function waitForScrollEnd(): Promise<void> {
    return new Promise((resolve: () => void) => {
        let settleTimer: number | undefined;

        const finish = (): void => {
            window.removeEventListener('scroll', restartSettleTimer, { capture: true });
            resolve();
        };
        const restartSettleTimer = (): void => {
            window.clearTimeout(settleTimer);
            settleTimer = window.setTimeout(finish, scrollSettleMs);
        };

        window.addEventListener('scroll', restartSettleTimer, { capture: true, passive: true });
        restartSettleTimer();
    });
}

// Scrolling closes the tooltip, so it is only opened once the dropdown is in view
async function scrollToSizeSelection(): Promise<void> {
    const dropdown = document.querySelector('[data-size-selection-required]');

    if (!dropdown) {
        return;
    }

    dropdown.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await waitForScrollEnd();
    sizeSelectionTooltip.value?.show();
}
</script>
